import React from 'react';
import {
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    AreaChart, Area, PieChart, Pie, Cell
} from 'recharts';

const data = [
    { name: 'Mon', interactions: 45, automated: 42 },
    { name: 'Tue', interactions: 52, automated: 48 },
    { name: 'Wed', interactions: 61, automated: 57 },
    { name: 'Thu', interactions: 58, automated: 55 },
    { name: 'Fri', interactions: 85, automated: 81 },
    { name: 'Sat', interactions: 95, automated: 91 },
    { name: 'Sun', interactions: 70, automated: 67 },
];

const topicData = [
    { name: 'Access', value: 400 },
    { name: 'House Rules', value: 300 },
    { name: 'Services', value: 200 },
    { name: 'Concierge', value: 100 },
];

const AdminDashboard = ({ theme }) => {
    const COLORS = theme === 'dark'
        ? ['#FFFFFF', '#52525b', '#27272a', '#18181b']
        : ['#000000', '#A1A1AA', '#E4E4E7', '#F4F4F5'];

    const gridStroke = theme === 'dark' ? '#27272a' : '#E5E7EB';
    const tickColor = theme === 'dark' ? '#52525b' : '#A1A1AA';
    const mainLineStroke = theme === 'dark' ? '#FFFFFF' : '#000000';
    const dashLineStroke = theme === 'dark' ? '#52525b' : '#A1A1AA';

    return (
        <div className={`rounded-3xl p-6 md:p-12 animate-fade-in shadow-2xl transition-all duration-500 border ${theme === 'dark' ? 'bg-zinc-950/50 border-zinc-900 shadow-black' : 'bg-white border-zinc-200 shadow-black/5'}`}>
            <div className={`flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6 border-b pb-10 transition-colors ${theme === 'dark' ? 'border-zinc-800' : 'border-zinc-100'}`}>
                <div>
                    <h3 className={`text-3xl font-bold mb-2 tracking-tighter transition-colors ${theme === 'dark' ? 'text-white' : 'text-black'}`}>Command Center</h3>
                    <p className="text-zinc-500 font-medium">Grand Horizon Suites • Real-time Operations</p>
                </div>
                <div className="flex items-center gap-4">
                    <div className={`px-4 py-2 border rounded-full text-xs font-bold uppercase tracking-widest transition-colors ${theme === 'dark' ? 'bg-zinc-900 border-zinc-800 text-zinc-400' : 'bg-zinc-100 border-zinc-200 text-zinc-500'}`}>
                        Live Stream
                    </div>
                    <div className={`w-3 h-3 rounded-full animate-pulse transition-colors ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}></div>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
                {[
                    { label: "Guest Satisfaction", value: "98.2%", trend: "↑ 2.4%" },
                    { label: "Automated Res.", value: "98.4%", trend: "Stable" },
                    { label: "Staff Time Saved", value: "142h", trend: "This month" },
                    { label: "Avg. Response", value: "< 1s", trend: "Instant" }
                ].map((stat, i) => (
                    <div key={i}>
                        <p className="text-[10px] uppercase tracking-widest font-bold text-zinc-500 mb-2">{stat.label}</p>
                        <h4 className={`text-4xl font-bold tracking-tighter transition-colors ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{stat.value}</h4>
                        <p className="text-xs text-zinc-600 mt-2 font-medium">{stat.trend}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
                <div className="lg:col-span-2">
                    <div className="flex items-center justify-between mb-8">
                        <h5 className="font-bold uppercase text-xs tracking-widest text-zinc-500">Volume Analysis</h5>
                        <div className="flex gap-4 text-[10px] font-bold uppercase">
                            <span className={`flex items-center gap-2 transition-colors ${theme === 'dark' ? 'text-white' : 'text-black'}`}><div className={`w-2 h-2 rounded-full ${theme === 'dark' ? 'bg-white' : 'bg-black'}`}></div> Total interactions</span>
                            <span className="flex items-center gap-2 text-zinc-500"><div className="w-2 h-2 rounded-full bg-zinc-400"></div> Handled automatically</span>
                        </div>
                    </div>
                    <div className="h-[300px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridStroke} />
                                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: tickColor, fontSize: 10, fontWeight: 'bold' }} dy={10} />
                                <YAxis hide />
                                <Tooltip
                                    contentStyle={{ backgroundColor: theme === 'dark' ? '#000' : '#fff', borderRadius: '12px', border: `1px solid ${gridStroke}`, color: theme === 'dark' ? '#fff' : '#000' }}
                                    itemStyle={{ color: theme === 'dark' ? '#fff' : '#000' }}
                                    itemSorter={() => -1}
                                />
                                <Area name="Total interactions" type="monotone" dataKey="interactions" stroke={mainLineStroke} strokeWidth={3} fillOpacity={0.1} fill={mainLineStroke} />
                                <Area name="Handled automatically" type="monotone" dataKey="automated" stroke={dashLineStroke} strokeWidth={2} strokeDasharray="5 5" fill="transparent" />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>

                <div className="flex flex-col justify-center">
                    <h5 className="font-bold uppercase text-xs tracking-widest text-zinc-500 mb-8">Interaction Distribution</h5>
                    <div className="h-[200px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie
                                    data={topicData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={80}
                                    paddingAngle={8}
                                    dataKey="value"
                                    stroke="none"
                                >
                                    {topicData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="space-y-4 mt-8">
                        {topicData.map((t, i) => (
                            <div key={i} className="flex items-center justify-between text-xs font-bold tracking-widest uppercase">
                                <span className="text-zinc-500">{t.name}</span>
                                <span className={`transition-colors ${theme === 'dark' ? 'text-white' : 'text-black'}`}>{t.value / 10}%</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;

